import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import EditToolModal from "../components/EditToolModal";
import AdminAutoAdd from "../components/AdminAutoAdd";
import AdminOgPreview from "../components/AdminOgPreview";
import AdminBulkExtractV2 from "../components/AdminBulkExtractV2";
import AdminLists from "../components/AdminLists";
import AdminExtension from "../components/AdminExtension";
import AdminUsers from "../components/AdminUsers";
import AdminEnrich from "../components/AdminEnrich";
import AdminPricing from "../components/AdminPricing";
import AdminBadges from "../components/AdminBadges";
import AdminXOutreach from "../components/AdminXOutreach";
import AdminConcierge from "../components/AdminConcierge";
import AdminAnalytics from "../components/AdminAnalytics";
import AdminStrategies from "../components/AdminStrategies";
import AdminSeo from "../components/AdminSeo";
import AdminVendors from "../components/AdminVendors";
import AdminMonetization from "../components/AdminMonetization";
import AdminActivity from "../components/AdminActivity";
import AdminOutreach from "../components/AdminOutreach";
import AdminOwnerCampaign from "../components/AdminOwnerCampaign";
import ApprovalEmailTest from "../components/ApprovalEmailTest";
import RebuildStaticButton from "../components/RebuildStaticButton";
import Seo from "../components/Seo";
import {
  adminListTools,
  adminSetStatus,
  adminDelete,
  adminUpdate,
  adminStats,
  fetchCategories,
  getAdminToken,
  clearAdminToken,
  authMe,
  authLogout,
} from "../lib/api";
import { domainOf, formatDate } from "../lib/utils-tv";
import { PRICING_OPTIONS } from "../components/PricingLegend";

const TABS = [
  { key: "pending", label: "pending" },
  { key: "approved", label: "approved" },
  { key: "rejected", label: "rejected" },
  { key: "all", label: "all" },
  { key: "auto-add", label: "+ auto-add" },
  { key: "og-preview", label: "+ og preview" },
  { key: "bulk-extract-v2", label: "+ bulk extract" },
  { key: "enrich", label: "+ enrich" },
  { key: "pricing", label: "+ pricing" },
  { key: "lists", label: "+ lists" },
  { key: "users", label: "+ users" },
  { key: "extension", label: "+ chrome extension" },
  { key: "badges", label: "+ badges" },
  { key: "x-outreach", label: "+ x outreach" },
  { key: "concierge", label: "+ concierge" },
  { key: "strategy", label: "+ strategy" },
  { key: "analytics", label: "+ analytics" },
  { key: "seo", label: "+ seo" },
  { key: "vendors", label: "+ vendors" },
  { key: "monetization", label: "+ monetization" },
  { key: "activity", label: "+ owner activity" },
  { key: "outreach", label: "+ outreach" },
  { key: "owner-invites", label: "+ owner invites" },
];

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [tools, setTools] = useState([]);
  const [editing, setEditing] = useState(null);
  const [ogPreviewPath, setOgPreviewPath] = useState("/");
  const [enrichFocus, setEnrichFocus] = useState(null); // { slug, nonce }
  const [assetClasses, setAssetClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [regions, setRegions] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [userBases, setUserBases] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [proSegments, setProSegments] = useState([]);
  const [pricingModels, setPricingModels] = useState([]);
  const [terminalTypes, setTerminalTypes] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, cats] = await Promise.all([
        adminStats(),
        fetchCategories(),
      ]);
      setStats(s);
      setAssetClasses(cats.asset_classes || []);
      setCategories(cats.categories || []);
      setInstruments(cats.instruments || []);
      setRegions(cats.regions || []);
      setSectors(cats.sectors || []);
      setUserBases(cats.user_bases || []);
      setDeliveryOptions(cats.delivery || []);
      setProSegments(cats.pro_segments || []);
      setPricingModels(cats.pricing_models || []);
      setTerminalTypes(cats.terminal_types || []);
      // The auto-add, og-preview, and bulk-extract-v2 tabs don't list tools.
      if (tab === "auto-add" || tab === "og-preview" || tab === "bulk-extract-v2" || tab === "enrich" || tab === "pricing" || tab === "lists" || tab === "extension" || tab === "badges" || tab === "x-outreach" || tab === "concierge" || tab === "strategy" || tab === "analytics" || tab === "seo" || tab === "vendors" || tab === "monetization" || tab === "activity" || tab === "outreach" || tab === "owner-invites") {
        setTools([]);
      } else {
        const list = await adminListTools(tab === "all" ? undefined : tab);
        setTools(list);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        nav("/admin/login");
        return;
      }
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
    // adminListTools/adminStats/fetchCategories/clearAdminToken are stable module
    // imports; setState setters are stable per React contract. Excluded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, nav]);

  useEffect(() => {
    // Two ways in: legacy password token (localStorage) OR a valid Emergent
    // Google Auth session cookie (verified server-side via /auth/me). The same
    // /auth/me result also surfaces the Google identity for the dashboard panel.
    let cancelled = false;
    (async () => {
      try {
        const me = await authMe();
        if (cancelled) return;
        setGoogleUser(me || null);
        if (getAdminToken() || (me && me.is_admin)) {
          setAuthed(true);
        } else {
          nav("/admin/login");
        }
      } catch {
        if (cancelled) return;
        if (getAdminToken()) setAuthed(true);
        else nav("/admin/login");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nav]);

  useEffect(() => {
    if (!authed) return undefined;
    // Defer to a microtask so load()'s initial setLoading(true) isn't a
    // synchronous state update inside the effect body.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) load();
    });
    return () => {
      cancelled = true;
    };
  }, [authed, load]);

  // Emergent-managed Google Auth. Redirect to the provider; it returns the user
  // to `<origin>/admin#session_id=<id>`, which AuthCallback exchanges.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const googleSignOut = async () => {
    try {
      await authLogout();
    } catch {
      /* no active google session */
    }
    setGoogleUser(null);
    toast.success("signed out of google");
  };

  const setStatus = async (id, status) => {
    try {
      await adminSetStatus(id, status);
      toast.success(`marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tool permanently?")) return;
    try {
      await adminDelete(id);
      toast.success("deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const updatePricing = async (id, pricing) => {
    try {
      await adminUpdate(id, { pricing });
      setTools((prev) => prev.map((t) => (t.id === id ? { ...t, pricing } : t)));
    } catch {
      toast.error("Pricing update failed");
    }
  };

  const updateAsset = async (id, asset_class) => {
    try {
      await adminUpdate(id, { asset_class });
      setTools((prev) => prev.map((t) => (t.id === id ? { ...t, asset_class } : t)));
    } catch {
      toast.error("Asset update failed");
    }
  };

  const toggleAffiliated = async (id, affiliated) => {
    try {
      await adminUpdate(id, { affiliated });
      setTools((prev) => prev.map((t) => (t.id === id ? { ...t, affiliated } : t)));
    } catch {
      toast.error("Affiliated update failed");
    }
  };

  const updateCat2 = async (id, category_2) => {
    try {
      await adminUpdate(id, { category_2 });
      setTools((prev) => prev.map((t) => (t.id === id ? { ...t, category_2: category_2 || null } : t)));
    } catch {
      toast.error("Secondary category update failed");
    }
  };

  const logout = async () => {
    clearAdminToken();
    try {
      await authLogout();
    } catch {
      /* no active Google session — fine */
    }
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-page">
      <Seo title="Admin" path="/admin" noindex />
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between border-b border-black pb-2">
          <h1 className="text-lg font-bold">{">"}_ admin / submissions</h1>
          <button className="tv-action-link danger" onClick={logout} data-testid="admin-logout-btn">[ logout ]</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3 text-[12px]" data-testid="admin-stats">
          <span className="tv-cat-pill">[pending {stats.pending}]</span>
          <span className="tv-cat-pill">[approved {stats.approved}]</span>
          <span className="tv-cat-pill">[rejected {stats.rejected}]</span>
          <Link
            to="/admin/inbox"
            className={`tv-cat-pill ${stats.messages_new ? "active" : ""}`}
            data-testid="inbox-link"
          >
            [inbox {stats.messages_new || 0}]
          </Link>
        </div>

        {/* Site login (Google) — Emergent-managed. Lives inside the admin so the
            operator can sign in / test the Google identity flow. */}
        <div
          className="mt-3 border border-black p-3 text-[12px] flex flex-wrap items-center gap-2"
          data-testid="admin-google-panel"
        >
          <span className="text-[#666]">site login (google):</span>
          {googleUser ? (
            <>
              <span className="font-bold" data-testid="admin-google-user">
                {googleUser.email}
              </span>
              <span className="text-[#bbb]">·</span>
              <button
                type="button"
                onClick={googleSignOut}
                className="tv-link"
                data-testid="admin-google-signout"
              >
                [ sign out ]
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={googleLogin}
              className="tv-btn flex items-center gap-2"
              data-testid="admin-google-login-btn"
            >
              <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
              </svg>
              sign in with google
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`tv-cat-pill ${tab === t.key ? "active" : ""}`}
              data-testid={`tab-${t.key}`}
            >
              [{t.label}]
            </button>
          ))}
          <button className="tv-cat-pill ml-auto" onClick={load} data-testid="admin-refresh-btn">[refresh]</button>
          <RebuildStaticButton />
        </div>

        <div className="mt-4 overflow-x-auto">
          {tab === "auto-add" ? (
            <AdminAutoAdd onSaved={() => load()} />
          ) : tab === "og-preview" ? (
            <AdminOgPreview initialPath={ogPreviewPath} />
          ) : tab === "bulk-extract-v2" ? (
            <AdminBulkExtractV2 />
          ) : tab === "enrich" ? (
            <AdminEnrich
              focus={enrichFocus}
              categories={categories}
              assetClasses={assetClasses}
              instruments={instruments}
              regions={regions}
              sectors={sectors}
              userBases={userBases}
              delivery={deliveryOptions}
              proSegments={proSegments}
              pricingModels={pricingModels}
            />
          ) : tab === "pricing" ? (
            <AdminPricing />
          ) : tab === "lists" ? (
            <AdminLists />
          ) : tab === "users" ? (
            <AdminUsers />
          ) : tab === "extension" ? (
            <AdminExtension />
          ) : tab === "badges" ? (
            <AdminBadges />
          ) : tab === "x-outreach" ? (
            <AdminXOutreach />
          ) : tab === "concierge" ? (
            <AdminConcierge />
          ) : tab === "strategy" ? (
            <AdminStrategies />
          ) : tab === "analytics" ? (
            <AdminAnalytics />
          ) : tab === "seo" ? (
            <AdminSeo
              onEnrich={(slug) => {
                setEnrichFocus({ slug, nonce: Date.now() });
                setTab("enrich");
              }}
            />
          ) : tab === "outreach" ? (
            <AdminOutreach />
          ) : tab === "owner-invites" ? (
            <AdminOwnerCampaign />
          ) : tab === "monetization" ? (
            <AdminMonetization onOpenVendors={() => setTab("vendors")} />
          ) : tab === "activity" ? (
            <AdminActivity />
          ) : tab === "vendors" ? (
            <AdminVendors />
          ) : loading ? (
            <div className="text-[13px] text-[#888] py-6" data-testid="admin-loading">loading...</div>
          ) : tools.length === 0 ? (
            <div className="text-[13px] text-[#888] py-6" data-testid="admin-empty">no items.</div>
          ) : (
            <>
            <ApprovalEmailTest />
            <datalist id="admin-pricing-suggestions">
              {PRICING_OPTIONS.map((p) => <option key={p} value={p} />)}
            </datalist>
            <table className="tv-table" data-testid="admin-table">
              <thead>
                <tr>
                  <th>date</th>
                  <th>title</th>
                  <th>url</th>
                  <th>submitter</th>
                  <th>cat</th>
                  <th>cat 2</th>
                  <th>aff</th>
                  <th>status</th>
                  <th>votes</th>
                  <th>actions</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.id} data-testid={`admin-row-${t.id}`}>
                    <td className="whitespace-nowrap">{formatDate(t.submitted_at)}</td>
                    <td>
                      <div className="font-bold">{t.title}</div>
                      <div className="text-[#555] text-[11px]">{t.description}</div>
                      <div className="text-[#888] text-[11px]">[key] {t.key_feature}</div>
                      {(!t.enriched_status || t.enriched_status === "none") && (
                        <div
                          className="text-[10px] font-bold text-[#b45309] mt-1"
                          data-testid={`unenriched-${t.id}`}
                        >
                          ⚠ un-enriched — run{" "}
                          <button
                            type="button"
                            onClick={() => {
                              setEnrichFocus({ slug: t.slug, nonce: Date.now() });
                              setTab("enrich");
                            }}
                            className="underline font-bold hover:text-black"
                            data-testid={`unenriched-draft-${t.id}`}
                            title="Open this tool in the enrich tab. Drafting never overwrites the author's description or choices."
                          >
                            [draft]
                          </button>
                        </div>
                      )}
                      {t.enriched_status && t.enriched_status !== "none" &&
                        (!t.alternatives || t.alternatives.length === 0) && (
                        <div
                          className="text-[10px] text-[#b45309] mt-1"
                          data-testid={`no-alts-${t.id}`}
                          title="Enriched, but the AI found no alternatives. Could be genuine (a one-of-a-kind tool) or a thin scrape — re-run [draft] to retry."
                        >
                          ⚠ no alternatives
                        </div>
                      )}
                      {t.auto_add_notes && (
                        <div
                          className="text-[10px] text-[#888] mt-1 italic"
                          data-testid={`auto-notes-${t.id}`}
                          title={t.auto_add_notes}
                        >
                          ai: {t.auto_add_notes}
                        </div>
                      )}
                    </td>
                    <td className="break-all">
                      <a href={t.url} target="_blank" rel="noopener noreferrer" className="tv-link">{domainOf(t.url)}</a>
                    </td>
                    <td className="text-[11px] whitespace-nowrap" data-testid={`submitter-${t.id}`}>
                      {t.submitter_email || t.submitter_name ? (
                        <div className="flex flex-col">
                          {t.submitter_name && (
                            <span className="text-[#222]">
                              {t.submitter_name}
                              {t.is_owner && <span className="ml-1 text-[10px] text-[#000] font-bold">[founder]</span>}
                            </span>
                          )}
                          {t.submitter_email ? (
                            <a
                              href={`mailto:${t.submitter_email}?subject=Your%20TerminalValue.io%20submission%3A%20${encodeURIComponent(t.title)}`}
                              className="tv-link"
                              title={t.submitter_email}
                            >
                              {t.submitter_email}
                            </a>
                          ) : (
                            !t.submitter_name && <span className="text-[#bbb]">—</span>
                          )}
                          {!t.submitter_name && t.is_owner && (
                            <span className="text-[10px] text-[#000] font-bold">[founder]</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#bbb]">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <select
                        className="tv-select py-0 px-1 text-[11px]"
                        value={t.asset_class || "Equities"}
                        onChange={(e) => updateAsset(t.id, e.target.value)}
                        data-testid={`asset-${t.id}`}
                        style={{ fontSize: 11, minWidth: 110 }}
                      >
                        {assetClasses.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </td>
                    <td className="whitespace-nowrap">{t.category}</td>
                    <td className="whitespace-nowrap">
                      <select
                        className="tv-select py-0 px-1 text-[11px]"
                        value={t.category_2 || ""}
                        onChange={(e) => updateCat2(t.id, e.target.value)}
                        data-testid={`cat2-${t.id}`}
                        style={{ fontSize: 11, minWidth: 110 }}
                      >
                        <option value="">—</option>
                        {categories.filter((c) => c !== t.category).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                    <td className="whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={!!t.affiliated}
                        onChange={(e) => toggleAffiliated(t.id, e.target.checked)}
                        className="accent-black cursor-pointer"
                        title="Flag as affiliated with TerminalValue.io"
                        data-testid={`affiliated-toggle-${t.id}`}
                      />
                    </td>
                    <td className="whitespace-nowrap">
                      <span className={
                        t.status === "approved" ? "text-[#0a7f00]" :
                        t.status === "rejected" ? "text-[#b00020]" : "text-[#000]"
                      }>{t.status}</span>
                    </td>
                    <td className="text-right tabular-nums">{t.upvotes}</td>
                    <td className="whitespace-nowrap">
                      <div className="flex flex-wrap gap-2">
                        <button className="tv-action-link" onClick={() => setEditing(t)} data-testid={`edit-${t.id}`}>[edit]</button>
                        {t.status === "approved" && t.slug && (
                          <button
                            className="tv-action-link"
                            onClick={() => {
                              setOgPreviewPath(`/tool/${t.slug}?_=${Date.now()}`);
                              setTab("og-preview");
                            }}
                            title="Preview OG share card"
                            data-testid={`og-preview-${t.id}`}
                          >
                            [og]
                          </button>
                        )}
                        {t.status !== "approved" && (
                          <button className="tv-action-link" onClick={() => setStatus(t.id, "approved")} data-testid={`approve-${t.id}`}>[approve]</button>
                        )}
                        {t.status !== "rejected" && (
                          <button className="tv-action-link" onClick={() => setStatus(t.id, "rejected")} data-testid={`reject-${t.id}`}>[reject]</button>
                        )}
                        {t.status !== "pending" && (
                          <button className="tv-action-link" onClick={() => setStatus(t.id, "pending")} data-testid={`pend-${t.id}`}>[pend]</button>
                        )}
                        <button className="tv-action-link danger" onClick={() => del(t.id)} data-testid={`delete-${t.id}`}>[delete]</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>
      </main>
      {editing && (
        <EditToolModal
          tool={editing}
          categories={categories}
          assetClasses={assetClasses}
          instruments={instruments}
          regions={regions}
          sectors={sectors}
          userBases={userBases}
          delivery={deliveryOptions}
          proSegments={proSegments}
          pricingModels={pricingModels}
          terminalTypes={terminalTypes}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setTools((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
            setEditing(null);
            toast.success("Saved");
          }}
        />
      )}
      <Footer />
    </div>
  );
}
